import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { getValueInUsd } from "@src/appV2/lib/Money/utils/currency";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { max, parseISO } from "date-fns";
import { useEffect } from "react";

import { getMinutesElapsedWithTargetArrival } from "../../ArrivalTime/calculateMinutesElapsed";
import { getUrgentArrivalPillLabel } from "../../ArrivalTime/getUrgentArrivalPillLabel";
import {
  SHIFT_DISCOVERY_SHIFT_AUCTION_CONFIRM_INSTANT_BOOK_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_AUCTION_REVIEW_BID_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { getScheduledShiftDurationInMinutes } from "../../utils/getScheduledShiftDuration";
import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { type ModalShiftData } from "../types";
import { SHIFT_AUCTION_BID_PAY_PARAMETER_NAME, SHIFT_AUCTION_LABEL } from "./constants";
import { PlaceBidBottomSheet } from "./PlaceBidBottomSheet";

export interface PlaceBidBottomSheetContainerProps {
  modalState: UseModalState;
  shift: ModalShiftData;
  inputBidPay?: number;
}

export function PlaceBidBottomSheetContainer(props: PlaceBidBottomSheetContainerProps) {
  const { modalState, shift, inputBidPay } = props;

  const {
    id: shiftId,
    workplace,
    shiftAuction,
    shiftAuctionBid,
    breakPolicy,
    attributes: { start, end, isInstantPay },
  } = shift;

  const {
    name: workplaceName,
    location: { timezone },
    requiresLunchBreak: workplaceRequiresLunchBreak,
  } = workplace.attributes;

  const requiresLunchBreak = (workplaceRequiresLunchBreak ?? false) && isDefined(breakPolicy);
  const mandatoryBreakDurationInMinutes = requiresLunchBreak
    ? (breakPolicy?.breakDurationInMinutes ?? 0)
    : 0;

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_AUCTION_PLACE_BID_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  const { navigateToModal, computedArrivalData } = useShiftModalsDataContext();

  const handlePlaceBid = async (pay: number) => {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_AUCTION_REVIEW_BID_MODAL_PATH, {
      pathParams: { shiftId },
      searchParams: { [SHIFT_AUCTION_BID_PAY_PARAMETER_NAME]: pay.toString() },
    });
  };

  const { showInfoToast } = useToast();

  const handleBack = () => {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
      pathParams: { shiftId },
      clearExistingSearchParams: true,
    });
  };

  // This may happen if the shift is no longer available for bidding
  useEffect(() => {
    if (!shiftAuction) {
      showInfoToast(`No ${SHIFT_AUCTION_LABEL} auction, returning to shift details`);
      logEvent(APP_V2_USER_EVENTS.SHIFT_AUCTION_PLACE_BID_NO_AUCTION, { shiftId });
      navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: true,
      });
    }
  }, [shiftAuction, shiftId, navigateToModal, showInfoToast]);

  const resolvedTimezone = timezone ?? "America/Los_Angeles";
  const urgentPillLabel = getUrgentArrivalPillLabel(
    computedArrivalData?.targetArrivalTime,
    resolvedTimezone
  );

  if (!shiftAuction) {
    return null;
  }

  const startDate = parseISO(start);
  const endDate = parseISO(end);

  const scheduledShiftDurationInHours = getScheduledShiftDurationInMinutes({ start, end }) / 60;
  const minutesElapsed = getMinutesElapsedWithTargetArrival({
    shiftStartDate: startDate,
    baseReferenceDate: max([shiftAuction.attributes.endTime, new Date()]),
    targetArrivalTime: computedArrivalData?.targetArrivalTime,
  });
  const remainingWorkDurationInHours = getShiftWorkDurationInHours(
    { start, end },
    Boolean(requiresLunchBreak),
    mandatoryBreakDurationInMinutes,
    minutesElapsed
  );

  const {
    averageWinningBid,
    averageTotalBidders,
    maximumPay,
    minimumPay,
    endTime: auctionEndTime,
    instantBookPay,
  } = shiftAuction.attributes;

  const minimumAuctionPay = getValueInUsd(minimumPay.amountInMinorUnits);
  const maximumAuctionPay = getValueInUsd(maximumPay.amountInMinorUnits);

  let initialSliderValue = minimumAuctionPay;

  const averageWinningBidInUsd = averageWinningBid
    ? getValueInUsd(averageWinningBid.amountInMinorUnits)
    : undefined;

  const auctionBidAmount = shiftAuctionBid
    ? getValueInUsd(shiftAuctionBid.attributes.bidAmount.amountInMinorUnits)
    : undefined;

  const instantBookPayInUsd = instantBookPay
    ? getValueInUsd(instantBookPay.amountInMinorUnits)
    : undefined;

  if (auctionBidAmount) {
    initialSliderValue = auctionBidAmount;
  } else if (inputBidPay && inputBidPay > initialSliderValue && inputBidPay <= maximumAuctionPay) {
    initialSliderValue = inputBidPay;
  }

  const handleInstantBook = () => {
    if (!instantBookPayInUsd) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.SHIFT_AUCTION_INSTANT_BOOK_BUTTON_CLICKED, { shiftId });
    navigateToModal(SHIFT_DISCOVERY_SHIFT_AUCTION_CONFIRM_INSTANT_BOOK_MODAL_PATH, {
      pathParams: { shiftId },
    });
  };

  return (
    <PlaceBidBottomSheet
      modalState={modalState}
      workplaceName={workplaceName}
      shiftStart={startDate}
      shiftEnd={endDate}
      timezone={resolvedTimezone}
      scheduledShiftDurationInHours={scheduledShiftDurationInHours}
      breakDurationInMinutes={mandatoryBreakDurationInMinutes}
      requiresBreak={Boolean(requiresLunchBreak)}
      remainingWorkDurationInHours={remainingWorkDurationInHours}
      minutesElapsed={minutesElapsed}
      auctionEndTime={auctionEndTime}
      averageWinningBid={averageWinningBidInUsd}
      averageTotalBidders={averageTotalBidders}
      minimumPay={minimumAuctionPay}
      maximumPay={maximumAuctionPay}
      isInstantPay={Boolean(isInstantPay)}
      isLoading={false}
      initialSliderValue={initialSliderValue}
      auctionBidAmount={auctionBidAmount}
      instantBookPayInUsd={instantBookPayInUsd}
      isInstantBookLoading={false}
      urgentPillLabel={urgentPillLabel}
      onPlaceBid={handlePlaceBid}
      onInstantBook={handleInstantBook}
      onBack={handleBack}
    />
  );
}
